<script lang="ts">
    // Painel de escolha de período: atalhos (Hoje, Semana atual, Mês passado...) e "Outro período" com De/Até e
    // setas. É o dropdown do date-select (mode="day") e do date-select-month (mode="month"), e o conteúdo do popup
    // de data do Zgrid2. Cores: --z2-* quando está dentro do Zgrid2; fora dele, os tokens globais de campo
    // (--z-field-*, css_z_field.css), que já acompanham o tema claro/escuro.
    // Trabalha numa cópia das datas: nada muda para quem usa até "aplicar" — fechar sem aplicar é cancelar.
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faCheck, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'

    export let from: Date
    export let to: Date
    export let mode: 'day' | 'month' = 'day'

    const dispatch = createEventDispatcher()

    let curFrom = new Date(from ?? Date.now())
    let curTo = new Date(to ?? Date.now())
    let showCustom = false

    type Shortcut = { key: string; label: string }
    const shortcutsByMode: Record<'day' | 'month', Shortcut[][]> = {
        day: [
            [
                { key: 'today', label: 'Hoje' },
                { key: 'yesterday', label: 'Ontem' },
                { key: 'tomorrow', label: 'Amanhã' },
            ],
            [
                { key: 'week-current', label: 'Semana atual' },
                { key: 'week-previous', label: 'Semana passada' },
                { key: 'week-next', label: 'Semana seguinte' },
            ],
            [
                { key: 'month-current', label: 'Mês atual' },
                { key: 'month-previous', label: 'Mês passado' },
                { key: 'month-next', label: 'Mês seguinte' },
            ],
        ],
        month: [
            [
                { key: 'month-current', label: 'Mês atual' },
                { key: 'month-previous', label: 'Mês passado' },
                { key: 'month-next', label: 'Mês seguinte' },
            ],
        ],
    }
    const fields: { field: 'from' | 'to'; label: string }[] = [
        { field: 'from', label: 'De' },
        { field: 'to', label: 'Até' },
    ]

    $: shortcuts = shortcutsByMode[mode]
    // unidade de comparação/edição: dia no seletor de datas, mês no de meses
    $: unitFormat = mode === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD'
    $: showFormat = mode === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY'
    $: inputType = mode === 'month' ? 'month' : 'date'
    // « e » andam a unidade maior (mês no de datas, ano no de meses); ‹ e › a menor
    $: arrowTitles = mode === 'month' ? ['Ano anterior', 'Mês anterior', 'Mês seguinte', 'Ano seguinte'] : ['Mês anterior', 'Dia anterior', 'Dia seguinte', 'Mês seguinte']
    // tudo o que a comparação usa vai como parâmetro: chamada de função no $: só reage ao que está escrito nela
    $: selectedShortcut = shortcutOf(curFrom, curTo, shortcuts, unitFormat)

    function datesOfShortcut(key: string): { from: Date; to: Date } {
        const dates = window.api.utils.dates
        switch (key) {
            case 'yesterday':
                return dates.yesterday()
            case 'tomorrow':
                return dates.tomorrow()
            case 'week-current':
                return dates.weekCurrent()
            case 'week-previous':
                return dates.weekPrevious()
            case 'week-next':
                return dates.weekNext()
            case 'month-current':
                return dates.currentMonth()
            case 'month-previous':
                return dates.previousMonth()
            case 'month-next':
                return dates.nextMonth()
            default:
                return dates.today()
        }
    }

    function shortcutOf(_from: Date, _to: Date, groups: Shortcut[][], format: string): string {
        const same = (a: Date, b: Date) => dayjs(a).format(format) === dayjs(b).format(format)
        for (const group of groups) {
            for (const item of group) {
                const dates = datesOfShortcut(item.key)
                if (same(dates.from, _from) && same(dates.to, _to)) return item.key
            }
        }
        return null
    }

    // troca de mês sem transbordar (31/jan + 1 mês = 28 ou 29/fev) e preservando "último dia do mês"
    function addMonths(date: Date, months: number): Date {
        const d = new Date(date)
        const originalDay = d.getDate()
        const isLastDay = originalDay === new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
        d.setDate(1)
        d.setMonth(d.getMonth() + months)
        const lastDayOfNewMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
        d.setDate(isLastDay ? lastDayOfNewMonth : Math.min(originalDay, lastDayOfNewMonth))
        return d
    }

    function step(field: 'from' | 'to', big: boolean, direction: 1 | -1) {
        const base = field === 'from' ? curFrom : curTo
        let next: Date
        if (mode === 'month') next = addMonths(base, big ? 12 * direction : direction)
        else next = big ? addMonths(base, direction) : dayjs(base).add(direction, 'day').toDate()
        if (field === 'from') curFrom = next
        else curTo = next
    }

    // campo nativo (AAAA-MM-DD ou AAAA-MM) -> Date local à meia-noite (no de meses, dia 1)
    function setField(field: 'from' | 'to', value: string) {
        if (!value) return
        const date = dayjs(value).toDate()
        if (field === 'from') curFrom = date
        else curTo = date
    }

    function applyShortcut(key: string) {
        const dates = datesOfShortcut(key)
        dispatch('apply', { from: dates.from, to: dates.to })
    }

    function applyCustom() {
        dispatch('apply', { from: curFrom, to: curTo })
    }
</script>

<div class="zds">
    {#if !showCustom}
        <div class="zds_head">
            <span class="zds_title">Período</span>
            <span class="zds_range">{dayjs(curFrom).format(showFormat)} – {dayjs(curTo).format(showFormat)}</span>
        </div>
        <div class="zds_list">
            {#each shortcuts as group, groupIndex}
                {#if groupIndex > 0}<div class="zds_divider"></div>{/if}
                {#each group as item}
                    <button type="button" class="zds_item" class:is-selected={selectedShortcut === item.key} on:click={() => applyShortcut(item.key)}>
                        <span class="zds_text">{item.label}</span>
                        {#if selectedShortcut === item.key}<span class="zds_item_icon"><Fa icon={faCheck} /></span>{/if}
                    </button>
                {/each}
            {/each}
            <div class="zds_divider"></div>
            <button type="button" class="zds_item" class:is-selected={!selectedShortcut} on:click={() => (showCustom = true)}>
                <span class="zds_text">Outro período...</span>
                <span class="zds_item_icon"><Fa icon={faChevronRight} /></span>
            </button>
        </div>
    {:else}
        <div class="zds_head">
            <button type="button" class="zds_back" on:click={() => (showCustom = false)}>
                <Fa icon={faChevronLeft} /><span class="zds_text">Atalhos</span>
            </button>
            <span class="zds_title">Outro período</span>
        </div>
        <div class="zds_custom">
            {#each fields as item}
                <div class="zds_field">
                    <span class="zds_field_label">{item.label}</span>
                    <div class="zds_field_row">
                        <button type="button" class="zds_nav" title={arrowTitles[0]} aria-label={arrowTitles[0]} on:click={() => step(item.field, true, -1)}>
                            <Fa icon={faAngleDoubleLeft} />
                        </button>
                        <button type="button" class="zds_nav" title={arrowTitles[1]} aria-label={arrowTitles[1]} on:click={() => step(item.field, false, -1)}>
                            <Fa icon={faAngleLeft} />
                        </button>
                        <input
                            class="zds_input"
                            type={inputType}
                            aria-label={item.label}
                            value={dayjs(item.field === 'from' ? curFrom : curTo).format(unitFormat)}
                            on:change={(event) => setField(item.field, event.currentTarget.value)}
                        />
                        <button type="button" class="zds_nav" title={arrowTitles[2]} aria-label={arrowTitles[2]} on:click={() => step(item.field, false, 1)}>
                            <Fa icon={faAngleRight} />
                        </button>
                        <button type="button" class="zds_nav" title={arrowTitles[3]} aria-label={arrowTitles[3]} on:click={() => step(item.field, true, 1)}>
                            <Fa icon={faAngleDoubleRight} />
                        </button>
                    </div>
                </div>
            {/each}
        </div>
        <div class="zds_foot">
            <button type="button" class="zds_apply" on:click={applyCustom}><span class="zds_text">Aplicar</span></button>
        </div>
    {/if}
</div>

<style>
    /* flex: 1 + min-height: 0 -> dentro de um popup que limita a altura à janela, a lista é quem rola */
    .zds {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
        font-size: 13px;
        color: var(--z2-text, var(--z-field-text));
    }
    /* mesmo ajuste de rótulo dos botões do Zgrid2: a Nunito deixa o texto ~1px acima do centro */
    .zds_text {
        padding-top: 2px;
    }
    .zds_head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 12px 14px 10px;
        border-bottom: 1px solid var(--z2-border-soft, var(--z-field-border));
    }
    .zds_title {
        font-size: 14px;
        font-weight: 600;
    }
    .zds_range {
        font-size: 12px;
        color: var(--z2-text-muted, var(--z-field-label));
        font-variant-numeric: tabular-nums;
    }
    /* sem altura fixa: os itens cabem inteiros; só rola se a janela for mais baixa que o popup */
    .zds_list {
        flex: 1 1 auto;
        min-height: 0;
        padding: 6px;
        overflow-y: auto;
    }
    .zds_divider {
        height: 1px;
        margin: 5px 6px;
        background: var(--z2-border-soft, var(--z-field-border));
    }
    .zds_item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        width: 100%;
        padding: 7px 10px;
        font-size: 13px;
        text-align: left;
        color: var(--z2-text, var(--z-field-text));
        background: transparent;
        border: 0;
        border-radius: 7px;
        cursor: pointer;
    }
    .zds_item:hover {
        background: var(--z2-bg-hover, var(--z-field-act-bg));
    }
    .zds_item.is-selected {
        font-weight: 600;
        color: var(--z2-accent-text, var(--z-tone-green));
    }
    .zds_item_icon {
        font-size: 11px;
        color: inherit;
        opacity: 0.8;
    }
    .zds_back {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0;
        font-size: 13px;
        font-weight: 500;
        color: var(--z2-accent-text, var(--z-tone-green));
        background: transparent;
        border: 0;
        cursor: pointer;
    }
    .zds_custom {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 14px;
    }
    .zds_field {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .zds_field_label {
        font-size: 12px;
        color: var(--z2-text-muted, var(--z-field-label));
    }
    .zds_field_row {
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .zds_nav {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 28px;
        height: 34px;
        padding: 0;
        font-size: 11px;
        color: var(--z2-text-muted, var(--z-field-label));
        background: var(--z2-bg, var(--z-field-bg));
        border: 1px solid var(--z2-border, var(--z-field-border));
        border-radius: 7px;
        cursor: pointer;
    }
    .zds_nav:hover {
        color: var(--z2-text, var(--z-field-text));
        background: var(--z2-bg-hover, var(--z-field-act-bg));
    }
    .zds_input {
        flex: 1 1 auto;
        min-width: 0;
        height: 34px;
        padding: 0 8px;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        color: var(--z2-text, var(--z-field-text));
        background: var(--z2-input, var(--z-field-bg));
        border: 1px solid var(--z2-border, var(--z-field-border));
        border-radius: 8px;
        outline: none;
        color-scheme: dark;
    }
    :global(.theme_light) .zds_input {
        color-scheme: light;
    }
    .zds_input:focus {
        border-color: var(--z2-accent, var(--z-field-focus));
        box-shadow: 0 0 0 3px var(--z2-accent-soft, var(--z-field-focus-ring));
    }
    .zds_foot {
        display: flex;
        justify-content: flex-end;
        padding: 10px 12px;
        border-top: 1px solid var(--z2-border-soft, var(--z-field-border));
    }
    /* o verde do botão principal serve nos dois temas: sem token global equivalente, a reserva é fixa */
    .zds_apply {
        display: inline-flex;
        align-items: center;
        height: 34px;
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        color: var(--z2-on-accent, #ffffff);
        background: var(--z2-accent-strong, #178557);
        border: 1px solid var(--z2-accent-strong, #178557);
        border-radius: 8px;
        cursor: pointer;
    }
    .zds_apply:hover {
        background: var(--z2-accent-hover, #1c9763);
        border-color: var(--z2-accent-hover, #1c9763);
    }
</style>
